import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {selectActiveWorkflow} from '@ispm/workflow/store/workflow-config.selectors';
import {DiagnosticOrderActionsService} from '@ispm/test/services/diagnostic-order-actions.service';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {get, isEmpty, set} from 'lodash';
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {IDiagnosticOrder} from '@ispm/test/interfaces/diagnostic-order.interface';
import {PollJobService} from '@ispm/test/services/poll-job.service';
import {WorkflowConfigService} from '@ispm/workflow/services/workflow-config.service';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {HttpInterceptorService} from '@ispm/core/services/http-interceptor.service';
import {ConfigurationStoreHandler} from '../../../app/configuration/services/configuration-store-handler.service';
import {IOptellumConfig} from '@ispm-client/modules/timeline/interfaces/timeline.interface';
import {WorkflowConfigState} from "@ispm/workflow/store/workflow-config.state";
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { map, take } from 'rxjs/operators';

const ACR_POST_URL = 'acr/validate';

@Injectable()
export class WorkflowUtilService {
  private resourceMapStore$: Store<WorkflowConfigState>;

  constructor(protected store: Store<IgenomicsState>,
              private authService: AuthenticationService,
              private diagnosticOrderActionsService: DiagnosticOrderActionsService,
              private _ispmHttpClient: IspmHttpClient,
              private notificationMessageService: NotificationMessageService,
              private workflowService: WorkflowService,
              private pollJobService: PollJobService,
              private workflowConfigService: WorkflowConfigService,
              private _coreAC: CoreActionCreatorService,
              private _http: HttpInterceptorService,
              private _configurationStoreHandler: ConfigurationStoreHandler) {
    this.resourceMapStore$ = store.select('workflowConfig') as any;
  }


  /**
   * Check current user is performer
   */
  public checkIfPerformer() {
    return this.store.pipe(select(selectActiveWorkflow)).map(currentWorkflow => {
      let isPerformer = false;
      if(currentWorkflow) {
        for (const ref of currentWorkflow.references) {
          if (ref.key && ref.key.toLowerCase() === 'practitioner') {
            try {
              const val = JSON.parse(ref.value);
              isPerformer = val && val.practType === 'performer' && val.practId === this.authService.user.info.id;
              if (isPerformer) {
                break;
              }
            } catch (e) {
              continue;
            }
          }
        }
      }
      return isPerformer;
    });
  }

  /**
   * Get current order, current and previous workflow
   * @param considerWorkflow
   */
  public getCurrentAndPreviousWorkflow(considerWorkflow): Observable<any> {
    return this.diagnosticOrderActionsService.getState().map(orders => {
      let currentOrder = get(orders, 'order');
      let currentWorkflow = get(currentOrder, 'workflowRequest');
      let previousWorkflow;
      let allWorkflowRequests = get(orders, 'allOrders').toArray();
      allWorkflowRequests = allWorkflowRequests.filter(temp => considerWorkflow ? considerWorkflow.includes(get(temp, 'name')) : get(temp, 'name') === get(currentWorkflow, 'name'));
      if (allWorkflowRequests.length > 1) {
        let currentWorkflowRequestIndex = allWorkflowRequests.findIndex(temp => get(temp,'id') == get(currentWorkflow,'id'));
        if (currentWorkflowRequestIndex > 0) {
          previousWorkflow = allWorkflowRequests[currentWorkflowRequestIndex - 1];
        }
      }
      return {currentOrder, currentWorkflow, previousWorkflow}
    });
  }

  /**
   *
   * @returns current diagnostic
   */
  public getCurrentWorkflow(): Observable<any>{
    return this.diagnosticOrderActionsService.getState().map(orders => {
      return get(orders, 'order');
    });
  }

 /* goToState(url: string) {
    const uriState: any = this.routerManagerService.urlToState(url);
    const currentApp = sessionStorage.getItem('currentApplication');
    if (currentApp) {
      const app = JSON.parse(currentApp);
      uriState.params.appname = get(app, 'alias', '');
    }
    this.stateService.go(uriState.name, uriState.params);
  }*/

  /**
   * Trigger ACR validation
   * @param payload optional
   * @param showLoader
   */
  public triggerACRValidation(payload?: any, showLoader: boolean = false) {
    const orderSubscription = combineLatest(
      this.diagnosticOrderActionsService.getState().take(1),
      this.resourceMapStore$.select((state: any) => state.resourcesMap).take(1),
      this._configurationStoreHandler.getCurrentConfig('Test').take(1),
      this.store.select('users', 'currentPatient').pipe(
        take(1),
        map(currentPatient => {
          /**
           * This is specifically handled for admin flow as the currentPatient would be empty in store
           */
          if (currentPatient == null || currentPatient == undefined) {
            return {};
          } else {
            return currentPatient;
          }
        }))
    ).subscribe(data => {
      const currentOrder = payload && payload.diagnosticOrder ? payload.diagnosticOrder : get(data, '[0].order');
      const resourceMap = get(data, '[1]');
      const currentUser = this.authService.user;
      const currentPatient = get(data, '[3]');
      const postPayload = payload || { };
      set(postPayload, 'patientId', get(currentOrder, 'subject'));
      set(postPayload, 'facilityChanged', get(payload, 'facilityChanged', false));
      set(postPayload, 'managingFacility', get(currentOrder, 'managingFacility', null));
      set(postPayload, 'diagnosticOrder', get(currentOrder, 'id'));
      set(postPayload, 'snapshotEditor', get(currentOrder, 'snapshotEditor', false));
      set(postPayload, 'sendValidationResult', true);
      set(postPayload, 'acr_validation', get(data, '2.acr_validation'));
      set(postPayload, 'workflowRequestId', get(resourceMap, 'workflowRequest'));
      set(postPayload, 'workflowRevisionId', get(resourceMap, 'requestedRevision'));
      set(postPayload, 'managingFacility', get(currentOrder, 'managingFacility', null));
      set(postPayload, 'patientName', `${currentPatient.fullName} (${currentPatient.MRN})`);
      set(postPayload, 'performer',
        {
          id: get(currentUser, 'info.id'),
          managingOrganization: get(currentUser, 'managingOrganization'),
          role: get(currentUser, 'role')
        });
      if (orderSubscription) {
        orderSubscription.unsubscribe();
      }
      if(get(currentOrder,'workflowRequest.latestRevisionStatus') !== "Eligibility") {
      const req = this._ispmHttpClient.post(ACR_POST_URL, postPayload, showLoader).map(acrStatus => {
          let validationArray = get(acrStatus, '[0].ACR_Validation.details');
          const workflowRequest = get(acrStatus, '[0].workflowRequest');
          const workflowRevision = get(acrStatus, '[0].workflowRevision');
          const patientName = get(acrStatus, '[0].patientName');
          const patient = get(acrStatus, '[0].patient');
          let validationErrorMessage = '';
          const redirectUrl = `/Pathologist/PatientProfile/${patient}/${workflowRevision}/${workflowRequest}/layoutEngine?appname=lcs`;

          if (get(acrStatus, '[0].ACR_Validation.valid') === true) {
            this.notificationMessageService.success(`${get(acrStatus, '[0].ACR_Status')}`, 5000, `<a href="${redirectUrl}"><b> ${patientName === 'undefined (undefined)' ? '' : patientName} </b></a>`);
          } else {
            let hasValidItems = false;
            if (get(acrStatus, 'validArray')) {
              validationArray = get(acrStatus, 'validArray');
              hasValidItems = true;
            }
            if (validationArray && validationArray.length > 0) {
              for (const validItem of validationArray) {
                validationErrorMessage += `${hasValidItems ? get(validItem, 'message', '') : validItem}`;
              }
            }
            this.notificationMessageService.warning(`${get(acrStatus, '[0].ACR_Status')}`, 5000, `<a href="${redirectUrl}"><b> ${patientName === 'undefined (undefined)' ? '' : patientName} </b></a><br/>${validationErrorMessage}`);
          }
          return acrStatus;
        },
        (acrStatus) => {
          let validationErrorMessage = `ACR Status: ${get(acrStatus, '[0].ACR_Status')}`;
          let validationArray = get(acrStatus, '[0].ACR_Validation.details');
          let hasValidItems = false;
          if (get(acrStatus, 'validArray')) {
            validationArray = get(acrStatus, 'validArray');
            hasValidItems = true;
          }
          if (validationArray && validationArray.length > 0) {
            for (const validItem of validationArray) {
              validationErrorMessage += ` ${hasValidItems ? get(validItem, 'message', '') : validItem}`;
            }
          }
          this.notificationMessageService.warning(`${validationErrorMessage}`);
        }).publish();
      req.connect();
      return req;
    }});
  }

  /**
   * Perform workflow step preparation
   * @param currentStepId - From which step perform workflow should be triggered
   * @param moveToStep - After perform workflow whether move to next step in the UI default true
   * @param params  - patientId, workflowRequestId/testId, revisionId
   * @param origin  - If origin other than workflow workflow request will be fetched based on workflowrequestId
   * @param callback - once performworkflow done or any error callback will be sent to callee
   */
  performWorkFlowStep(currentStepId, moveToStep: boolean = true, params?: any, origin?: string, callback?: any) {
    this._coreAC.setLoader(true);
    if (origin === 'workflow') {
      if (params) {
        this.callPojJobToPerformWorkflowStep(params, currentStepId, moveToStep, callback);
      } else {
        let orderSubscription = combineLatest(
          this.diagnosticOrderActionsService.getState().take(1),
          this.resourceMapStore$.select((state: any) => state.resourcesMap).take(1)
        ).subscribe(data => {
          let currentOrder = get(data, '[0].order');
          let resourceMap = get(data, '[1]');
          if (!resourceMap || !currentOrder) {
            return;
          }
          const params: any = {};
          params.patientId = get(currentOrder, 'subject');
          params.testId = get(resourceMap, 'workflowRequest');
          params.revisionId = get(resourceMap, 'requestedRevision');
          this.callPojJobToPerformWorkflowStep(params, currentStepId, moveToStep, callback, orderSubscription);
        });
      }
    } else {
      if (params) {
        this._http.get('/workflow/find?id=' + params.testId, true).subscribe(resp => {
          let workflow = resp.body;
          if(workflow && workflow.activeJobName)
            if ((typeof currentStepId === 'string' && workflow.activeJobName == currentStepId) ||
              (Array.isArray(currentStepId) && currentStepId.includes(workflow.activeJobName))
            ) {
              workflow['viewRevisionId'] = params.revisionId;
              this.workflowConfigService.setActiveWorkflow(workflow);
              this.workflowConfigService.setResourcesMap({
                requestedRevision: workflow.activeRevision,
                activeJob: workflow.activeJob
              });
              this.callPojJobToPerformWorkflowStep(params, currentStepId, moveToStep, callback);
            } else {
              this._coreAC.setLoader(false);
              if (callback && typeof callback === 'function') {
                callback(undefined);
              }
            }
        });
        return;
      }
    }
  }

  /**
   * Call pol job service to perform workflow
   * @param params  - patientId, workflowRequestId/testId, revisionId
   * @param currentStepId - From which step perform workflow should be triggered
   * @param moveToStep - After perform workflow whether move to next step in the UI default true
   * @param callback - once performworkflow done or any error callback will be sent to callee
   * @param orderSubscription - current order subscription
   */
  callPojJobToPerformWorkflowStep(params, currentStepId, moveToStep, callback?: any, orderSubscription?: Subscription) {
    //First getting order data to check what is the current step (the worklist row contains step display text but we cannot count on the display so have to get order data)
    this.workflowService.getPatientAnalyses(params, true, false).then(testResponse => {
      let currentTest: IDiagnosticOrder = testResponse.body.current;
      //Move to any step if current step is 'any'
      let activeJobName = testResponse.body.current.workflowRequest.activeJobName;
      if ((typeof currentStepId === 'string' && testResponse.body.current.workflowRequest.activeJobName == currentStepId) ||
        (Array.isArray(currentStepId) && currentStepId.includes(activeJobName))
      ) {
        this.pollJobService.performWorkflowStepByStepName(
          activeJobName,
          null,
          currentTest,
          undefined,
          'async',
          undefined,
          (stepData) => {
            this._coreAC.setLoader(false);
            if (stepData && stepData.resource) { //update form data with the updated workflow step
              if (callback && typeof callback === 'function') {
                callback(stepData);
              }
            }
            if (orderSubscription) {
              orderSubscription.unsubscribe();
            }
          },
          moveToStep
        )
      } else {
        this._coreAC.setLoader(false);
        if (callback && typeof callback === 'function') {
          callback(undefined);
        }
      }
    });
  }

  /**
   * It queries multiple resources and builds an optellum configuration object
   * @returns an object used to construct Optellum URL and validates permissions
   */
  getOptellumConfigurationInfo(): IOptellumConfig {
    let optellumConfigInfoObject = <IOptellumConfig>{};
    this.store.select('events','timelineSelectedEvent')
      .take(1)
      .subscribe((data) => {
        set(optellumConfigInfoObject, 'modality', get(data, 'category.code'));
        set(optellumConfigInfoObject, 'eventInfo',  get(data, 'id'));
        const content: any[] = get(data, 'content');
        if (!isEmpty(content)) {
          const imagingInfo = content.find(val => val.formKey === "imagingInfo");
          if (!isEmpty(imagingInfo)) {
            set(optellumConfigInfoObject, 'accessionNumber', get(imagingInfo, 'data.accessionNumber'));
          }
        }
      });

    this.store.select('users','currentPatient')
      .take(1)
      .subscribe((data) => {
        set(optellumConfigInfoObject, 'mrn', get(data, 'MRN'));
      });

    this.store.select('tests','diagnosticOrders')
      .take(1)
      .subscribe((data) => {
        set(optellumConfigInfoObject, 'testNumber', get(data, 'order.identifiers[0].value'));
        set(optellumConfigInfoObject, 'testType', get(data, 'order.testType'));
        set(optellumConfigInfoObject, 'appName', get(data, 'order.originator.service.code'));
      });
    return optellumConfigInfoObject;
  }
}


